/**
 * Keeps alarm cards synchronized with real-time recurrence and device-state events.
 *
 * Flow:
 * 1. Identify each alarm by its source host and ID.
 * 2. Accept device-state patches only when they are newer than the rendered state.
 * 3. Create missing reactive device containers before applying status and color fields.
 * 4. Preserve a newer local visual state when an older full alarm response arrives.
 * 5. Keep recurrence counters monotonic while applying their related device state.
 */
const defaultHost = () => typeof window !== "undefined"
  ? window.__env?.VUE_APP_BASE_URL || ""
  : "";

/** Returns the stable identity used to match alarms from different servers. */
export const alarmIdentity = (alarm) =>
  JSON.stringify([alarm?.host || defaultHost(), Number(alarm?.id)]);

/**
 * Applies a newer device-state event to the alarm currently rendered by Vue.
 * Missing nested device containers are created through Vue's setter so the
 * status and color changes remain reactive.
 */
export const applyAlarmDeviceState = (alarm, payload, set) => {
  if (!alarm || !payload?.id || alarmIdentity(alarm) !== alarmIdentity(payload)) return false;
  const version = Number(payload.device_state_version);
  const currentVersion = Number(alarm.device_state_version) || 0;
  if (payload.device_state_version != null) {
    if (!Number.isSafeInteger(version) || version <= currentVersion) return false;
  } else if (currentVersion) {
    return false;
  }
  const camera = payload.detection_event_data?.camera_data?.camera;
  if (camera) {
    if (!alarm.detection_event_data) set(alarm, "detection_event_data", {});
    if (!alarm.detection_event_data.camera_data) set(alarm.detection_event_data, "camera_data", {});
    if (!alarm.detection_event_data.camera_data.camera) {
      set(alarm.detection_event_data.camera_data, "camera", {});
    }
    Object.entries(camera).forEach(([key, value]) => {
      set(alarm.detection_event_data.camera_data.camera, key, value);
    });
  }
  if (payload.panel_device) {
    if (!alarm.panel_device) set(alarm, "panel_device", {});
    Object.entries(payload.panel_device).forEach(([key, value]) => {
      set(alarm.panel_device, key, value);
    });
  }
  if (Object.prototype.hasOwnProperty.call(payload, "automatic_restored_at")) {
    set(alarm, "automatic_restored_at", payload.automatic_restored_at);
  }
  if (payload.device_state_version != null) set(alarm, "device_state_version", version);
  return true;
};

/** Keeps a locally received device state when a stale full response replaces an alarm. */
export const preserveAlarmDeviceState = (alarm, previous, set) => {
  if (Number(previous?.device_state_version) > (Number(alarm?.device_state_version) || 0)) {
    applyAlarmDeviceState(alarm, previous, set);
  }
};

/** Updates the recurrence count without allowing an older event to lower it. */
export const applyAlarmRecurrence = (alarm, payload, set) => {
  const count = Number(payload?.recurrence_count);
  if (!alarm || !payload?.id || payload.recurrence_count == null
    || !Number.isInteger(count) || count < 0
    || alarmIdentity(alarm) !== alarmIdentity(payload)) return false;
  const currentCount = Number(alarm.recurrence_count) || 0;
  if (count > currentCount || Number(payload.device_state_version) > (Number(alarm.device_state_version) || 0)) {
    applyAlarmDeviceState(alarm, payload, set);
  }
  set(alarm, "recurrence_count", Math.max(currentCount, count));
  return true;
};
